import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

export const THEME_STORAGE_KEY = "tride-theme";

/**
 * Light / dark / follow-the-OS, resolved to exactly one of two answers.
 *
 * Three settings, two outcomes: `preference` is what the user chose ("system" included) and
 * `theme` is what that resolves to right now. Components almost always want `theme` — the only
 * thing that needs `preference` is the control that sets it, which has to show "System" as
 * distinct from whichever of the two it currently means.
 *
 * The resolved value is mirrored onto `<html data-theme>` rather than kept in React alone, so
 * plain CSS can switch on it without every styled element subscribing to this context.
 */
const ThemeContext = createContext({
  theme: "light",
  preference: "system",
  setPreference: () => {},
});

const systemTheme = () =>
  typeof window !== "undefined"
    && window.matchMedia?.("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";

const storedPreference = () => {
  if (typeof window === "undefined") return "system";
  try {
    const v = window.localStorage.getItem(THEME_STORAGE_KEY);
    return v === "light" || v === "dark" ? v : "system";
  } catch (_) {
    // Private browsing / storage disabled. Following the OS is a fine place to land.
    return "system";
  }
};

export const ThemeProvider = ({ children }) => {
  // Start on "system" during SSR and the first paint so the server-rendered markup matches what
  // React expects; the inline script in gatsby-ssr has already painted the right colours by then,
  // and the effect below reconciles state to it without a second flash.
  const [preference, setPreferenceState] = useState("system");
  const [system, setSystem] = useState("light");

  useEffect(() => {
    setPreferenceState(storedPreference());
    setSystem(systemTheme());
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e) => setSystem(e.matches ? "dark" : "light");
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const theme = preference === "system" ? system : preference;

  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    root.dataset.theme = theme;
    // Tells the browser to render its own furniture — scrollbars, form controls, the URL bar on
    // mobile — to match. Without it a dark page keeps a white scrollbar down the side.
    root.style.colorScheme = theme;
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", theme === "dark" ? "#0b1120" : "#e20074");
  }, [theme]);

  const setPreference = (next) => {
    setPreferenceState(next);
    try {
      if (next === "system") window.localStorage.removeItem(THEME_STORAGE_KEY);
      else window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch (_) { /* nothing to do — the choice still holds for this session */ }
  };

  const value = useMemo(
    () => ({ theme, preference, setPreference }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme, preference]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);
